<template>
  <ListboxOption
    v-slot="{ active }"
    :value="option"
    v-bind="{ disabled }"
    as="template"
  >
    <li
      :class="[
        active
          ? 'cursor-pointer bg-gray-lightest/30 text-gray-900'
          : 'cursor-pointer text-gray-900',
        option.value === selectedOption.value ? 'bg-gray-lightest/40' : '',
      ]"
      class="relative flex select-none justify-between border-b-2 border-gray-lightest px-3 py-2 last:border-b-0"
    >
      <div class="flex items-center gap-1.5">
        <span
          :class="[
            option.value === selectedOption.value
              ? 'font-semibold'
              : 'font-normal',
          ]"
          class="w-full"
        >
          {{ option.label }}
        </span>

        <BadgeLabel
          v-if="option.isDefault || option.value === sizeStore.mostChosenSku"
          :label="$t('pages.configurator.sizes.most_chosen')"
          class="text-no-wrap hidden shrink-0 self-auto truncate bg-order-buttons/20 text-order-buttons min-[321px]:block"
        />

        <BadgeLabel
          v-if="
            !selectedOption.isDefault &&
            selectedOption.predefinedShowText &&
            showPredefinedShowText
          "
          :label="selectedOption.predefinedShowText"
          class="text-no-wrap hidden shrink-0 self-auto truncate bg-order-buttons/20 text-order-buttons min-[321px]:block"
        />
      </div>

      <FilterPrices
        v-if="showPrices || isAmountFilter"
        v-bind="{
          option,
          isAmountFilter,
        }"
      />
    </li>
  </ListboxOption>
</template>

<script setup lang="ts">
import type { SelectorOption } from '@/types/Configurator/SelectorOption';
import { computed, type ComputedRef, type PropType } from 'vue';
import { ListboxOption } from '@headlessui/vue';
import FilterPrices from '@/pages/ProductPage/partials/ProductFilters/FilterPrices.vue';
import BadgeLabel from '@/components/BadgeLabel.vue';
import {
  amountHexagonsKey,
  useFilterStore,
} from '@/stores/Configurator/FilterStore.ts';
import { useSizeStore } from '@/stores/Configurator/SizeStore.ts';

const props = defineProps({
  option: {
    type: Object as PropType<SelectorOption>,
    required: true,
  },

  selectedOption: {
    type: Object as PropType<SelectorOption>,
    required: true,
  },

  showPrices: {
    type: Boolean,
    required: false,
    default: true,
  },

  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },

  index: {
    type: [String, Number] as PropType<string | number>,
    required: false,
    default: undefined,
  },

  isShowingPredefinedShowText: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const sizeStore = useSizeStore();
const filterStore = useFilterStore();

const showPredefinedShowText: ComputedRef<boolean> = computed(
  () =>
    props.isShowingPredefinedShowText &&
    filterStore.defaultFilterSkus[props.index ?? 0] === props.option?.value
);

const isAmountFilter = computed(() => props.index === amountHexagonsKey);
</script>
